import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, ModuleWithProviders, NgModule } from '@angular/core';
import {
  provideAnimations,
  provideNoopAnimations,
} from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { CoreModule } from '@rd/core/core.module';
import {
  apiValidationFactory,
  ApiValidationService,
} from '@rd/core/services/api-validation/api-validation.service';
import { AssetMap } from '@rd/core/services/asset-service/asset.service';
import * as RD_LIB_TOKENS from '@rd/core/tokens/rd-lib.token';
import { RdSpinnerComponent } from '@rd/shared-legacy/components/spinner/spinner.component';
import { PrismicConfig } from '@rd/types';
import { disableAnimations } from './animations.config';
import {
  RdLibModuleCookiesConfig,
  RdLibModuleHermesModeConfig,
  RdLibModuleIdleCheckConfig,
} from './rd-lib.module.config';
import { SentryErrorHandler } from './sentry/sentry-error-handler';
import { SentryService } from './sentry/sentry.service';

export interface RdLibModuleConfig {
  // Bootstrap config
  defaultCurrencyCode: string;
  defaultLocaleCode: string;
  nydusNetworkApiBaseUrl: string;
  env: string; // Only used for logging purposes
  production: boolean; // Only used for logging purposes
  guardhouseApiUrl: string;
  clientId: string;
  connection: string;
  scope: string;
  redirectUri: string;
  travelEdgeBaseUrl: string;

  // Tenant-specifc app settings
  brandName: string;
  portalName: string;
  complimentaryNightBaseUrl: string | null;
  includeClosingPointsAccounts?: boolean;
  isWebview?: boolean;
  openExternalLinkInOwnTab?: boolean;
  shortBrandName: string;
  stripeApiKey?: string;
  tenantName: string;
  tenantPrivacyPolicyUrl?: string;
  tenantPrivacyStatementUrl?: string;
  tenantTncsUrl?: string;
  renderCategoryIcon?: boolean;
  cashNumberDigitsInfo?: string;
  pointsNumberDigitsInfo?: string;
  pointsTransactionDigitsInfo?: string;
  useCustomPageTitle?: boolean;

  // Guardhouse-related config
  hermesMode: RdLibModuleHermesModeConfig;
  idleCheckConfig: RdLibModuleIdleCheckConfig;
  sessionCheckPollInterval: number;
  cookiesConfig: RdLibModuleCookiesConfig;
  supportedLocales: string[];
  appId: string;

  // Points activity config
  pointsTransactionEnabled?: boolean;

  // Asset service config
  assetMap?: AssetMap;

  // Prismic config, optional for existing whitelabels that has static pages on TE
  prismicConfig?: PrismicConfig;
}

@NgModule({
  imports: [CommonModule, CoreModule, RouterModule, RdSpinnerComponent],
  exports: [CoreModule, RdSpinnerComponent],
  providers: [
    disableAnimations ? provideNoopAnimations() : provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    SentryService,
    {
      provide: ApiValidationService,
      useFactory: apiValidationFactory,
      deps: [RD_LIB_TOKENS.PRODUCTION],
    },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
  ],
})
export class RdLibModule {
  static forRoot(config: RdLibModuleConfig): ModuleWithProviders<RdLibModule> {
    return {
      ngModule: RdLibModule,
      providers: [
        {
          provide: RD_LIB_TOKENS.NYDUS_NETWORK_API_BASE_URL,
          useValue: config.nydusNetworkApiBaseUrl,
        },
        {
          provide: RD_LIB_TOKENS.DEFAULT_CURRENCY_CODE,
          useValue: config.defaultCurrencyCode,
        },
        {
          provide: RD_LIB_TOKENS.DEFAULT_LOCALE_CODE,
          useValue: config.defaultLocaleCode,
        },
        {
          provide: RD_LIB_TOKENS.ENV,
          useValue: config.env,
        },
        {
          provide: RD_LIB_TOKENS.PRODUCTION,
          useValue: config.production,
        },
        {
          provide: RD_LIB_TOKENS.GUARDHOUSE_API_URL,
          useValue: config.guardhouseApiUrl,
        },
        {
          provide: RD_LIB_TOKENS.CLIENT_ID,
          useValue: config.clientId,
        },
        {
          provide: RD_LIB_TOKENS.CONNECTION,
          useValue: config.connection,
        },
        {
          provide: RD_LIB_TOKENS.SCOPE,
          useValue: config.scope,
        },
        {
          provide: RD_LIB_TOKENS.REDIRECT_URI,
          useValue: config.redirectUri,
        },
        {
          provide: RD_LIB_TOKENS.TRAVEL_EDGE_BASE_URL,
          useValue: config.travelEdgeBaseUrl,
        },
        {
          provide: RD_LIB_TOKENS.TENANT_PRIVACY_POLICY_URL,
          useValue: config.tenantPrivacyPolicyUrl ?? null,
        },
        {
          provide: RD_LIB_TOKENS.TENANT_PRIVACY_STATEMENT_URL,
          useValue: config.tenantPrivacyStatementUrl ?? null,
        },
        {
          provide: RD_LIB_TOKENS.TENANT_TNCS_URL,
          useValue: config.tenantTncsUrl ?? null,
        },
        {
          provide: RD_LIB_TOKENS.COMPLIMENTARY_NIGHT_BASE_URL,
          useValue: config.complimentaryNightBaseUrl,
        },
        {
          provide: RD_LIB_TOKENS.IS_WEBVIEW,
          useValue: config.isWebview ?? false,
        },
        {
          provide: RD_LIB_TOKENS.BRAND_NAME,
          useValue: config.brandName,
        },
        {
          provide: RD_LIB_TOKENS.PORTAL_NAME,
          useValue: config.portalName,
        },
        {
          provide: RD_LIB_TOKENS.OPEN_EXTERNAL_LINK_IN_OWN_TAB,
          useValue: config.openExternalLinkInOwnTab ?? false,
        },
        {
          provide: RD_LIB_TOKENS.SHORT_BRAND_NAME,
          useValue: config.shortBrandName,
        },
        {
          provide: RD_LIB_TOKENS.STRIPE_API_KEY,
          useValue: config.stripeApiKey ?? '',
        },
        {
          provide: RD_LIB_TOKENS.TENANT_NAME,
          useValue: config.tenantName,
        },
        {
          provide: RD_LIB_TOKENS.HERMES_MODE,
          useValue: config.hermesMode,
        },
        {
          provide: RD_LIB_TOKENS.IDLE_CHECK_CONFIG,
          useValue: config.idleCheckConfig,
        },
        {
          provide: RD_LIB_TOKENS.INCLUDE_CLOSING_POINTS_ACCOUNTS,
          useValue: config.includeClosingPointsAccounts ?? false,
        },
        {
          provide: RD_LIB_TOKENS.SESSION_CHECK_POLL_INTERVAL,
          useValue: config.sessionCheckPollInterval,
        },
        {
          provide: RD_LIB_TOKENS.COOKIES_CONFIG,
          useValue: config.cookiesConfig,
        },
        {
          provide: RD_LIB_TOKENS.SUPPORTED_LOCALES,
          useValue: config.supportedLocales,
        },
        {
          provide: RD_LIB_TOKENS.POINTS_TRANSACTION_ENABLED,
          useValue: config.pointsTransactionEnabled ?? true,
        },
        {
          provide: RD_LIB_TOKENS.PRISMIC_CONFIG,
          useValue: config.prismicConfig,
        },
        {
          provide: RD_LIB_TOKENS.CASH_NUMBER_DIGITS_INFO,
          useValue: config.cashNumberDigitsInfo,
        },
        {
          provide: RD_LIB_TOKENS.POINTS_NUMBER_DIGITS_INFO,
          useValue: config.pointsNumberDigitsInfo,
        },
        {
          provide: RD_LIB_TOKENS.POINTS_TRANSACTION_DIGITS_INFO,
          useValue: config.pointsTransactionDigitsInfo,
        },
        {
          provide: RD_LIB_TOKENS.USE_CUSTOM_PAGE_TITLE,
          useValue: config.useCustomPageTitle ?? false,
        },
        {
          provide: RD_LIB_TOKENS.APP_ID,
          useValue: config.appId,
        },
        {
          provide: RD_LIB_TOKENS.ASSET_MAP,
          useValue: config.assetMap,
        },
        {
          /**
           * Angular Material 21 / CDK 21 render overlays (select panels, menus,
           * autocompletes) as native popovers inserted next to their trigger
           * instead of teleporting them into `body > .cdk-overlay-container`.
           *
           * Our styling predates that: panel rules are scoped under
           * `.cdk-overlay-container`, and components that hide their host
           * `mat-select` (the auto-redemption date pickers) leak
           * `visibility: hidden` into an inline panel. Opting out keeps the
           * pre-upgrade DOM placement for every tenant.
           */
          provide: OVERLAY_DEFAULT_CONFIG,
          useValue: { usePopover: false },
        },
      ],
    };
  }
}
